import type { EventBasedMarketView, TemplateView } from '@ps/isp-web-services-types';
import type { ReactNode } from 'react';
import React from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '@swf/core/store';
import { ExpandablePanel, Title } from '@swf/sports/components';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import { isSingleMarketTemplate } from '@swf/sports/utils/isSingleMarketTemplate';

import {
  TXTWCL_Featured_Request_A_Bet,
  TXTWCL_Request_A_Bet,
  TXTWCL_odds_boost
} from './SpecialsTemplatePanel.messages.json';
import styles from './SpecialsTemplatePanel.scss';
import TemplateGroup from './components/TemplateGroup/TemplateGroup';
import { TitleIcon } from './components/TitleIcon/TitleIcon';
import { isTemplateViewsGroupedByDate } from './utils/isTemplateViewsGroupedByDate';
import {
  featuredRequestABetMarketTypes,
  oddsBoostMarketTypes,
  requestABetMarketTypes
} from './utils/marketTypes';

export type TemplateViewsGroupedByDate = TemplateView[][];
export type TemplateViewsGroupedByMarket = TemplateView[];

interface Props {
  panel: TemplateViewsGroupedByDate | TemplateViewsGroupedByMarket;
  isFirstPanel: boolean;
}

interface ContentProps extends Props {
  isGroupedByDate: boolean;
  firstMarket: EventBasedMarketView;
}

const TemplatePanelContent: React.FC<ContentProps> = ({
  firstMarket,
  isFirstPanel,
  isGroupedByDate,
  panel
}) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();

  const event = useSelector((state: RootState) => widgetSelect.event(state, firstMarket.eventId));
  const isOddsBoost = oddsBoostMarketTypes.includes(firstMarket.marketType);
  const isRequestABet = requestABetMarketTypes.includes(firstMarket.marketType);
  const isFeaturedRequestABet = featuredRequestABetMarketTypes.includes(firstMarket.marketType);

  let title: ReactNode = <span>{event.eventName}</span>;

  if (isOddsBoost) {
    title = <TitleIcon name="price-boost" text={TXTWCL_odds_boost} />;
  } else if (isRequestABet) {
    title = <TitleIcon name="rab" text={TXTWCL_Request_A_Bet} />;
  } else if (isFeaturedRequestABet) {
    title = <TitleIcon name="rab" text={TXTWCL_Featured_Request_A_Bet} />;
  }

  return (
    <div className={styles.wrapper} key={event.eventName}>
      <ExpandablePanel
        isOpenInitially
        isNested
        title={<Title isNested>{title}</Title>}
        isOddsBoost={isOddsBoost}
      >
        <div className={styles.panelWrapper}>
          {panel.map((templatesOrTemplate, templatesIndex) => {
            const templates = Array.isArray(templatesOrTemplate)
              ? templatesOrTemplate
              : [templatesOrTemplate];
            const singleMarketTemplates = templates
              .map(({ template }) => template)
              .filter(isSingleMarketTemplate);

            return (
              <TemplateGroup
                isGroupedByDate={isGroupedByDate}
                isOpen={isFirstPanel && templatesIndex === 0}
                templates={singleMarketTemplates}
                key={`${event.eventSlug}-${templatesIndex}`}
              />
            );
          })}
        </div>
      </ExpandablePanel>
    </div>
  );
};

const TemplatePanel: React.FC<Props> = ({ panel, isFirstPanel }) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const isGroupedByDate = isTemplateViewsGroupedByDate(panel);
  const firstTemplateInPanel = isGroupedByDate ? panel[0][0].template : panel[0].template;
  const firstMarketId = isSingleMarketTemplate(firstTemplateInPanel)
    ? firstTemplateInPanel.marketId
    : undefined;
  const firstMarket = useSelector((state: RootState) =>
    widgetSelect.market(state, firstMarketId ?? '')
  );

  return firstMarket ? (
    <TemplatePanelContent
      firstMarket={firstMarket}
      isFirstPanel={isFirstPanel}
      isGroupedByDate={isGroupedByDate}
      panel={panel}
    />
  ) : null;
};

export default TemplatePanel;
